import { ref, type Ref } from 'vue';
import { useBankAccountStore } from '@/stores/bank-account.store';
import { debounce } from 'lodash';

interface UseBankAccountSelectOptions {
  customerId: Ref<any>;
  selectedId: Ref<any>;
  pageSize?: number;
  debounceMs?: number;
}

export function useBankAccountSelect(options: UseBankAccountSelectOptions) {
  const { customerId, selectedId, pageSize = 100, debounceMs = 300 } = options;
  const { getBankAccountsList, getBankAccountById } = useBankAccountStore();

  const bankAccounts = ref<any[]>([]);
  const loading = ref(false);
  const hasMore = ref(false);
  const nextPage = ref<number | null>(null);
  const lastSearch = ref('');

  async function ensureSelectedInList() {
    const id = selectedId.value;
    if (id == null) return;
    const numericId = typeof id === 'object' ? (id as any).id : id;
    if (numericId == null) return;
    const found = bankAccounts.value.some(
      (b: any) => b.id === numericId || String(b.id) === String(numericId)
    );
    if (found) return;
    try {
      const bankAccount = await getBankAccountById(Number(numericId));
      bankAccounts.value = [bankAccount as any, ...bankAccounts.value];
    } catch {
      // Bank account might have been deleted
    }
  }

  async function fetchBankAccounts(search?: string) {
    if (!customerId.value) return;
    loading.value = true;
    lastSearch.value = search || '';
    try {
      const response = await getBankAccountsList({
        page_size: pageSize,
        customer: customerId.value,
        customer_accounts_only: true,
        ordering: 'bank_name',
        search: search || undefined,
      });
      bankAccounts.value = [...response.results];
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
      if (!search) await ensureSelectedInList();
    } finally {
      loading.value = false;
    }
  }

  async function loadMore() {
    if (!nextPage.value || loading.value || !customerId.value) return;
    loading.value = true;
    try {
      const response = await getBankAccountsList({
        page_size: pageSize,
        page: nextPage.value,
        customer: customerId.value,
        customer_accounts_only: true,
        ordering: 'bank_name',
        search: lastSearch.value || undefined,
      });
      bankAccounts.value = [...bankAccounts.value, ...response.results];
      nextPage.value = response.next_page_number;
      hasMore.value = !!response.next_page_number;
    } finally {
      loading.value = false;
    }
  }

  const onSearch = debounce((query: string) => {
    fetchBankAccounts(query);
  }, debounceMs);

  return {
    bankAccounts,
    loading,
    hasMore,
    fetchBankAccounts,
    loadMore,
    onSearch,
  };
}
